import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/currency';
import { Plus, Trash2, Receipt, TrendingDown } from 'lucide-react';
import { PageHeader, Card, StatTile, EmptyState, DataTable, Toolbar } from '../components/ui';

const CATEGORIES = ['maintenance','repair','insurance','taxes','utilities','other'];

const Expenses = () => {
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState({ by_category: [], total: 0 });
    const [properties, setProperties] = useState([]);
    const [filter, setFilter] = useState({ property_id:'', category:'', from:'', to:'' });
    const [showForm, setShowForm] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            for (const [k, v] of Object.entries(filter)) if (v) params.append(k, v);
            const [e, t, p] = await Promise.all([
                axios.get(`/api/expenses?${params.toString()}`),
                axios.get(`/api/expenses/totals?${params.toString()}`),
                axios.get('/api/properties').catch(() => ({ data: { properties: [] } })),
            ]);
            setRows(e.data.expenses || []);
            setTotals(t.data || { by_category: [], total: 0 });
            setProperties(p.data.properties || []);
        } catch { toast.error('Failed to load'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const remove = async id => {
        if (!window.confirm('Delete this expense?')) return;
        try { await axios.delete(`/api/expenses/${id}`); toast.success('Deleted'); load(); }
        catch { toast.error('Failed'); }
    };

    return (
        <div className="animate-fade-in">
            <PageHeader
                title="Expenses"
                subtitle="Costs incurred on your properties."
                actions={
                    <button onClick={() => setShowForm(true)} className="btn-primary">
                        <Plus className="h-4 w-4" /> New expense
                    </button>
                }
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <StatTile label="Total (filtered)" value={formatCurrency(totals.total)} icon={TrendingDown} accent="warning" />
                <Card title="By category" className="lg:col-span-2">
                    {totals.by_category.length === 0
                      ? <div className="text-neutral-400 text-sm">No expenses in this range</div>
                      : (
                          <div className="space-y-2">
                              {totals.by_category.map(c => {
                                  const pct = totals.total > 0 ? (Number(c.total) / totals.total * 100) : 0;
                                  return (
                                      <div key={c.category}>
                                          <div className="flex justify-between text-xs mb-1">
                                              <span className="capitalize text-neutral-700 font-medium">{c.category}</span>
                                              <span className="tabular-nums text-neutral-900">{formatCurrency(c.total)}</span>
                                          </div>
                                          <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                                              <div className="h-full bg-primary-600" style={{ width: `${pct}%` }} />
                                          </div>
                                      </div>
                                  );
                              })}
                          </div>
                      )}
                </Card>
            </div>

            <Card padding={false}>
                <div className="p-4 pb-0">
                    <Toolbar
                        filters={
                            <>
                                <select className="input py-1.5 text-xs w-auto" value={filter.property_id} onChange={e => setFilter({...filter, property_id: e.target.value})}>
                                    <option value="">All properties</option>
                                    {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                                <select className="input py-1.5 text-xs w-auto" value={filter.category} onChange={e => setFilter({...filter, category: e.target.value})}>
                                    <option value="">All categories</option>
                                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <input type="date" className="input py-1.5 text-xs w-auto" value={filter.from} onChange={e => setFilter({...filter, from: e.target.value})} />
                                <input type="date" className="input py-1.5 text-xs w-auto" value={filter.to} onChange={e => setFilter({...filter, to: e.target.value})} />
                            </>
                        }
                    />
                </div>
                {loading ? (
                    <div className="p-8 text-neutral-400 text-sm text-center">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="p-8">
                        <EmptyState icon={Receipt}
                            title="No expenses match"
                            description="Try clearing filters, or record a new expense."
                            action={<button onClick={() => setShowForm(true)} className="btn-primary">
                                <Plus className="h-4 w-4" /> New expense
                            </button>} />
                    </div>
                ) : (
                    <DataTable
                        density="compact"
                        columns={[
                            { key: 'expense_date', label: 'Date', sortable: true,
                              render: r => new Date(r.expense_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) },
                            { key: 'property_name', label: 'Property', sortable: true },
                            { key: 'category', label: 'Category', sortable: true, render: r => <span className="capitalize">{r.category}</span> },
                            { key: 'description', label: 'Description' },
                            { key: 'vendor', label: 'Vendor' },
                            { key: 'amount', label: 'Amount', align: 'right', sortable: true,
                              render: r => <span className="font-medium text-neutral-900">{formatCurrency(r.amount)}</span> },
                            { key: 'action', label: '',
                              render: r => <button onClick={() => remove(r.id)} className="text-danger-600 p-1 hover:bg-danger-50 rounded">
                                  <Trash2 className="h-4 w-4" />
                              </button> },
                        ]}
                        rows={rows}
                        rowKey={r => r.id}
                        pageSize={25}
                        emptyMessage="No expenses."
                    />
                )}
            </Card>

            {showForm && <ExpenseForm properties={properties} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
        </div>
    );
};

const ExpenseForm = ({ properties, onClose, onSaved }) => {
    const today = new Date().toISOString().slice(0, 10);
    const [f, setF] = useState({ property_id:'', category:'maintenance', description:'', amount:'', expense_date: today, vendor:'', notes:'' });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setF(x => ({ ...x, [k]: v }));
    const submit = async e => {
        e.preventDefault(); setSaving(true);
        try { await axios.post('/api/expenses', f); toast.success('Expense recorded'); onSaved(); }
        catch (err) { toast.error(err.response?.data?.message || 'Failed'); setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-neutral-900/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-xl max-w-lg w-full shadow-lg animate-slide-up">
                <div className="px-5 py-4 border-b border-neutral-100 flex justify-between items-center">
                    <h2 className="text-sm font-semibold">New expense</h2>
                    <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-700">✕</button>
                </div>
                <div className="p-5 grid grid-cols-2 gap-3">
                    <F label="Property" span2>
                        <select required className="input" value={f.property_id} onChange={e => set('property_id', e.target.value)}>
                            <option value="">Select…</option>
                            {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </F>
                    <F label="Category"><select className="input" value={f.category} onChange={e => set('category', e.target.value)}>{CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></F>
                    <F label="Date"><input required type="date" className="input" value={f.expense_date} onChange={e => set('expense_date', e.target.value)} /></F>
                    <F label="Description" span2><input required className="input" value={f.description} onChange={e => set('description', e.target.value)} /></F>
                    <F label="Amount (KES)"><input required type="number" step="0.01" className="input" value={f.amount} onChange={e => set('amount', e.target.value)} /></F>
                    <F label="Vendor"><input className="input" value={f.vendor} onChange={e => set('vendor', e.target.value)} /></F>
                    <F label="Notes" span2><textarea rows={2} className="input" value={f.notes} onChange={e => set('notes', e.target.value)} /></F>
                </div>
                <div className="px-5 py-4 border-t border-neutral-100 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                    <button disabled={saving} className="btn-primary">Save</button>
                </div>
            </form>
        </div>
    );
};
const F = ({ label, children, span2 }) => (
    <div className={span2 ? 'col-span-2' : ''}>
        <label className="block text-xs font-medium text-neutral-700 mb-1">{label}</label>{children}
    </div>
);

export default Expenses;
